import { Component, Input, ViewChild, OnInit, OnDestroy, EventEmitter, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, timer } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';
import { MatSidenav } from '@angular/material/sidenav';
import { MatDialog } from '@angular/material/dialog';
import { AgGridAngular } from 'ag-grid-angular';
import {
  SideBarDef,
  ColDef,
  Column,
  GetContextMenuItemsParams,
  MenuItemDef,
  IDetailCellRendererParams,
  SelectionChangedEvent,
  GridOptions,
  GridReadyEvent,
  ProcessCellForExportParams,
} from 'ag-grid-community';
import { ColumnState } from 'ag-grid-community/dist/lib/columns/columnModel';

// Services
import { CommonService, DataExportExt, FileExtensionEnum } from '../../services/common.service';
import {
  ReportsService,
  ReportMeta,
  ColumnSortModel,
  ColumnGroupLabel,
  SsrTotalRows,
} from '../../services/reports.service';
import { CustomContentService } from 'src/app/services/custom-content.service';
import { PredefinedFiltersService } from 'src/app/services/predefined-filters.service';
import { ExportGridService } from 'src/app/services/export-grid.service';
import { GridStateService } from 'src/app/services/gridstate.service';

// Components
import { PromptWithActionDialogComponent } from '../../dialogs/prompt-with-action/prompt-with-action.component';

// Models
import { Config } from 'src/app/config';
import { ReportOption, ServerSideRenderingOptions } from '../../model/reports';
import {
  customAgGridComponents,
  ICustomAgGridComponents,
} from 'src/app/model/ag-grid-custom-models/custom-components-config';
import { defaultDetailGridConfig, DetailGridConfig } from 'src/app/model/ag-grid-custom-models/detail-grid-config';
import { ColumnsService } from '../../services/columns.service';

export interface ReportInformation {
  notification?: string;
  disableDPR?: boolean;
  alertContent?: string | null;
  alertDisplayAuthorisation?: boolean | null;
  columnGroupLabels?: ColumnGroupLabel[];
  encryptedPin?: boolean;
  hidePinFromDpr?: boolean;
  recommendationsReport?: {
    intervention: string;
    dates: {
      timezone: string;
      generated: string;
      refreshed: string;
    };
  };
  refreshMenuNotificationCountType?: string;
  benchmarkRow?: {
    flaggedColumn: string;
  };
  pinnedBottomRow?: {
    type: string;
  };
}

@Component({
  selector: 'vrm-mentor-reports',
  templateUrl: './vrm-mentor-reports.component.html',
  styleUrls: ['./vrm-mentor-reports.component.scss'],
})
export class VrmMentorReportsComponent implements OnInit, OnDestroy {
  @Input() serverSideRendering?: ServerSideRenderingOptions;
  @Input() metadata: ReportMeta;
  @Input() rawData: Array<any> = [];
  @Input() pinnedTopRowData: Array<any> = [];
  @Input() pinnedBottomRowData: Array<any> = [];
  @Input() reportTitle: string;
  @Input() reportOptions: ReportOption;
  @Input() multiRowSelection: boolean = false;
  @Input() canDrilldownToDriverPerformanceRecord: boolean = true;
  @Input() primaryKey: string;
  @Input() canAddElement: boolean = false;
  @Input() canUploadElements: boolean = false;
  @Input() canDelete: boolean = false;
  @Input() csvOption: any;
  @Input() dataRowSized: boolean = false; // for dynamic grid row height - Jay Noh
  @Input() controlRowSelection: boolean = false;
  @Output() addNewElement: EventEmitter<any> = new EventEmitter();
  @Output() administerMisContent: EventEmitter<any> = new EventEmitter();
  // Available only if canUpload is true
  @Output() upload: EventEmitter<any> = new EventEmitter();
  @Output() makeRowEditable: EventEmitter<any> = new EventEmitter();
  @Output() deleteElement: EventEmitter<any> = new EventEmitter();
  @Output() externalAction: EventEmitter<any> = new EventEmitter();
  @Output() elementAction: EventEmitter<any> = new EventEmitter();
  @Input() externalActionName: string;

  @Output() userSelected: EventEmitter<any> = new EventEmitter();
  @Output() rowSelected: EventEmitter<any> = new EventEmitter();
  showRenderedGrid: boolean = false;

  agMobileColumns: ColDef[] = [];

  columnsToDisplay: ColDef[];
  agGridDefaultColumnConfig: ColDef;

  initialLoad: boolean = true;
  initialView: string;
  reportHasInformation: boolean = false;
  reportInformation: ReportInformation = {};
  gridToolPaneShowing: boolean = true;

  minSsrSearchChar: number = 3;
  serverDataDownloadComplete: boolean = false;
  serverDataDownloadFailed: boolean = false;
  serverDataDownloadSubscriptions: Array<Subscription> = [];

  currentFilters: any = {};
  queryParamFilters: any = {};
  originalKeyHasUnderscore: boolean = false;

  customComponents: ICustomAgGridComponents = customAgGridComponents;
  sideBar: SideBarDef;
  statusBar: any;
  gridOptions: GridOptions;

  detailColumnsToDisplay: ColDef[];
  detailGridConfig: DetailGridConfig = defaultDetailGridConfig;

  private icons: any;

  private subscriptions = {
    commonReportDataChangedOnLoad: null,
    commonReportDataChanged: null,
    commonReportMetaChanged: null,
    commonReportPageChanged: null,
    newlySelectedRowAdded: null,
    headerSearchToggled: null,
    headerSearchInputChanged: null,
    serverSideSearchOnTextChanged: null,
    reportDownloadRequest: null,
    dprDialogClosed: null,
    serverFilter: null,
    reportDataChanged: null,
  };

  @ViewChild('agGrid', { static: true }) agGrid: AgGridAngular;
  @ViewChild('sidenav', { static: false }) sidenav: MatSidenav;
  doubleClicked: boolean;
  disableStationAssignment: boolean = Config.disableStationAssignment;

  constructor(
    private dialog: MatDialog,
    public cService: CommonService,
    public rptService: ReportsService,
    public cContentService: CustomContentService,
    private translate: TranslateService,
    private activeRoute: ActivatedRoute,
    private reportService: ReportsService,
    public predefinedFiltersService: PredefinedFiltersService,
    public gridStateService: GridStateService,
    public exportGridService: ExportGridService,
    private columnsService: ColumnsService
  ) {
    this.subscriptions.commonReportDataChangedOnLoad = rptService.commonReportDataChangedOnLoad$.subscribe({
      next: () => {
        rptService.selectedRows = [];

        this.hideGridInitially();

        if (!this.initialLoad) {
          setTimeout(() => {
            this.setDefaultFilters();
          });

          console.log('%c -- Common Report Data Changed --', 'color: lime');
        }

        setTimeout(() => {
          this.cService.setPageHeader(this.reportTitle, true, true);
          this.makeGridVisibleOnceClientFilterDone();
        });
      },
    });

    this.subscriptions.commonReportMetaChanged = rptService.commonReportMetaChanged$.subscribe({
      next: () => {
        this.hideGridInitially();
        setTimeout(() => {
          this.rptService.dataStructure = this.metadata.structure;
          this.columnsToDisplay = this.rptService.convertToAgColumnDefinition(
            this.metadata.structure,
            this.metadata.name
          );

          if (this.metadata.detailStructure) {
            this.configureDetailGrid();
          }

          if (!this.initialLoad) {
            this.agGrid.columnDefs = this.columnsToDisplay;
          }
          console.log('%c -- Common Report Meta Changed --', 'color: lime');
          this.makeGridVisibleOnceClientFilterDone();
        });
      },
    });

    this.initialView = cService.isPcViewport ? 'pc' : 'mobile';
  }

  private configureDetailGrid(): void {
    this.detailColumnsToDisplay = this.rptService.convertToAgColumnDefinition(
      this.metadata.detailStructure,
      this.metadata.name
    );

    this.detailGridConfig.detailGridRendererParams = {
      detailGridOptions: {
        columnDefs: this.detailColumnsToDisplay,
        components: this.customComponents,
        ...this.rptService.agGridConfig.common,
        excelStyles: this.rptService.agGridConfig.excelStyles,
        rowClassRules: this.rptService.agGridConfig.rowClassRules,
        onFirstDataRendered: function (params: any) {
          params.api.sizeColumnsToFit();
        },
      },
      getDetailRowData: function (params: any) {
        params.successCallback(params.data.detailData);
      },
    } as IDetailCellRendererParams;

    this.detailGridConfig.detailExcelExportParams = {
      getCustomContentBelowRow: this.exportGridService.exportExcelDetailDataBelowRow,
    };
  }

  public canRowExpandToShowDetailGrid(rowData: any): boolean {
    return rowData.detailData && rowData.detailData.length > 0;
  }

  ngOnInit() {
    this.rptService.setGrid(this.agGrid); //https://vrm-mentor.atlassian.net/browse/V3DDP-338
    this.setDefaultFilters();

    this.subscriptions.newlySelectedRowAdded = this.rptService.newlySelectedRowAdded$.subscribe({
      next: (respData) => {
        if (respData) {
          this.multiRowSelection
            ? this.rptService.selectedRows.push(respData)
            : (this.rptService.selectedRows = [respData]);

          let selectedRowCheckedInGrid = false;
          this.agGrid.api.forEachNode((node) => {
            if (!selectedRowCheckedInGrid && node.data[this.primaryKey] === respData[this.primaryKey]) {
              node.setSelected(true);
              selectedRowCheckedInGrid = true;
            }
          });

          const driver = this.rawData.find((row) => row[this.primaryKey] === respData[this.primaryKey]);
          if (driver) {
            driver.selected = true;
          }
        }

        this.getQueryParamAndCollateFilters();
      },
    });

    this.reportService.rowsDeleted$.subscribe((rowData) => {
      this.deleteRows(rowData);
    });

    this.reportService.rowAdded$.subscribe((rowData) => {
      this.addRow(rowData);
    });

    this.reportService.rowUpdated$.subscribe((rowData) => {
      this.updateRow(rowData);
    });

    /* ====================================
  Shared Page-header events subscriptions
  ====================================== */
    this.subscriptions.headerSearchToggled = this.cService.headerSearchToggled$.subscribe({
      next: () => {
        if (this.agGrid.api) {
          this.rptService.gridSearch.text = '';
          this.clearSearch();
        }
      },
    });

    if (this.serverSideRendering) {
      if (this.subscriptions.headerSearchInputChanged) {
        this.subscriptions.headerSearchInputChanged.unsubscribe();
      }

      this.subscriptions.serverSideSearchOnTextChanged = this.cService.headerSearchInputChanged$
        .pipe(debounceTime(1000))
        .subscribe({
          next: (searchText: string) => {
            if (!this.initialLoad) {
              if (searchText && searchText.split('').length >= this.minSsrSearchChar) {
                this.searchOrClearServerSideSearch();
              }
            }
          },
        });
    } else {
      if (this.subscriptions.serverSideSearchOnTextChanged) {
        this.subscriptions.serverSideSearchOnTextChanged.unsubscribe();
      }

      this.subscriptions.headerSearchInputChanged = this.cService.headerSearchInputChanged$.subscribe({
        next: (searchText: string) => {
          if (this.agGrid.api && !this.serverSideRendering) {
            this.searchTextChanged();
          }
        },
      });
    }

    this.subscriptions.serverFilter = this.rptService.serverFilterChanged$.subscribe({
      next: () => {
        if (this.serverSideRendering) {
          this.agGrid.api.refreshServerSide();
        }

        this.agGrid.api.addEventListener('rowDataUpdated', () => {
          this.restoreStateAcrossGrids();
        });
      },
    });

    this.subscriptions.dprDialogClosed = this.rptService.driverPerformanceDialogClosed$.subscribe({
      next: () => {
        if (!this.initialLoad) {
          if (this.serverSideRendering) {
            this.agGrid.api.refreshServerSide();
          }

          this.agGrid.api.addEventListener('rowDataUpdated', () => {
            this.restoreStateAcrossGrids();
          });
        }
      },
    });

    this.subscriptions.reportDataChanged = this.rptService.reportDataChanged$.subscribe({
      next: () => {
        if (!this.initialLoad) {
          if (this.serverSideRendering) {
            this.agGrid.api.refreshServerSide();
            this.clearRowSelections();
          }

          this.agGrid.api.addEventListener('rowDataUpdated', () => {
            this.restoreStateAcrossGrids();
          });
        }
      },
    });

    this.subscriptions.reportDownloadRequest = this.cService.downloadReportTriggered$.subscribe({
      next: (extension: DataExportExt) => {
        if (!this.initialLoad) {
          if (this.serverSideRendering && this.serverSideRendering.useServerSideRendering) {
            this.initializeServerSideReportDownload(extension);
          } else {
            this.exportReportToRequestedExtension(extension);
          }
        }
      },
    });
    // -- End

    this.initializeAgGridProperties();
  }

  /* AG-GRID LIFECYCLE HOOKS
  ------------------------ */
  initializeAgGridProperties() {
    // Used to specify Ag-Grid specific sub components

    this.icons = {
      settings: '<span class="ag-icon ag-icon-settings"></span>',
      details: '<span class="ag-icon ag-icon-details"></span>',
    };

    if (this.reportOptions && this.reportOptions.sideBar) {
      this.sideBar = this.reportOptions.sideBar;
    }

    this.statusBar = {
      statusPanels:
        this.serverSideRendering && this.serverSideRendering.useServerSideRendering
          ? [
              { statusPanel: 'statusBarRecommendationActionsComponent', align: 'left' },

              { statusPanel: 'customStatusBarFilteredCountComponent', align: 'left' },
              { statusPanel: 'customStatusBarRowCountComponent', align: 'left' },

              { statusPanel: 'customStatusBarSelectedCountComponent', align: 'left' },
            ]
          : [
              { statusPanel: 'statusBarRecommendationActionsComponent', align: 'left' },

              { statusPanel: 'agFilteredRowCountComponent', align: 'left' },
              { statusPanel: 'agTotalRowCountComponent', align: 'left' },

              { statusPanel: 'agSelectedRowCountComponent', align: 'left' },
            ],
    };

    const defaultExportParams = {
      processCellCallback: (params: ProcessCellForExportParams) => {
        const completeColumnDefinition = this.columnsService.getColumnDefinition(params.column.getColId());

        if (completeColumnDefinition) {
          return this.reportService.getOutputForExport(params);
        }

        return params.value;
      },
    };

    // Should come last mostly
    this.gridOptions = {
      includeHiddenColumnsInQuickFilter: true,
      defaultColDef: this.rptService.agGridConfig.defaultColumnConfig,
      rowGroupPanelShow: 'onlyWhenGrouping',
      rowBuffer: 50,
      rowSelection: this.multiRowSelection ? 'multiple' : 'single',
      suppressRowClickSelection: false,
      suppressAggFuncInHeader: !this.cService.convertTruthyFalsyPermToBoolean(
        this.cService.userProfile.companySettings.showGroupAggregateIndicator
      ),
      statusBar: this.statusBar,
      excelStyles: this.rptService.agGridConfig.excelStyles,
      defaultCsvExportParams: defaultExportParams,
      defaultExcelExportParams: defaultExportParams,
      icons: this.icons,
      context: {
        componentParent: this,
      },
      rowClassRules: this.rptService.agGridConfig.rowClassRules,
      getRowId: (params) => {
        return params.data?.id ? params.data.id : params.data.AGGridId;
      },
      ...this.rptService.agGridConfig.common,
    };

    if (this.serverSideRendering) {
      this.gridOptions.blockLoadDebounceMillis = 1500;
    }

    if (this.controlRowSelection) {
      this.gridOptions.isRowSelectable = (rowNode) => (rowNode.data ? rowNode.data.selectable : false);
    }

    this.activateServerSideRenderingOnDemand(true);

    if (this.dataRowSized) {
      this.gridOptions.getRowHeight = this.getRowHeight(this.cService.reportGridDefult.defaultHeight);
    }
  }

  getRowHeight(defaultHeight: number) {
    return function (params) {
      let rowHeight = defaultHeight;

      if (params.data && params.data.columnsToResize) {
        params.data.columnsToResize.forEach((element) => {
          if (params.data[element]) {
            if (params.data[element].length * defaultHeight > rowHeight) {
              rowHeight = params.data[element].length * defaultHeight;
            }
          }
        });
      }

      return rowHeight;
    };
  }

  triggerOutputAction() {
    this.externalAction.emit();
  }

  triggerElementAction(element) {
    this.elementAction.emit(element);
  }

  getOutputActionName(): string {
    return this.externalActionName;
  }

  rowDataChanged() {
    if (this.dataRowSized) {
      setTimeout(() => {
        this.agGrid.api.sizeColumnsToFit();
      }, 10);
    }
  }

  agGridReady(event: GridReadyEvent) {
    this.activateServerSideRenderingOnDemand();

    this.displayColumnsByViewportSize();

    const columnsWithEvents = this.columnsService.getColumnsWithEventsForReport(this.metadata.structure);
    Object.entries(columnsWithEvents).forEach((column) => {
      const [columnName, columnDefinition] = column;

      Object.entries(columnDefinition.events).forEach((columnEvent) => {
        const [eventName, eventFunction] = columnEvent;

        if (this.agGrid.hasOwnProperty(eventName)) {
          if (eventName === 'gridReady') {
            eventFunction(event);
          } else {
            let subscriptionId = `column_${columnName}_${eventName}`;
            this.subscriptions[subscriptionId] = this.agGrid[eventName].subscribe((event: unknown) =>
              eventFunction(event)
            );
          }
        }
      });
    });

    this.agGrid.virtualColumnsChanged.subscribe(() => {
      this.getQueryParamAndCollateFilters();
    });

    setTimeout(() => {
      this.initialLoad = false;
      this.initializeCurrentFilterModel();
    }, 2500);

    console.log('%c agGrid Ready', 'color: lightblue');
  }

  customAgGridContextMenuItems(params: GetContextMenuItemsParams): Array<string | MenuItemDef> {
    const result: Array<string | MenuItemDef> = ['copy', 'copyWithHeaders', 'paste', 'separator', 'chartRange'];

    return result;
  }

  private initializeCurrentFilterModel(): void {
    this.currentFilters = this.agGrid.api.getFilterModel();
  }

  private initializeServerSideReportDownload(extension: DataExportExt) {
    const totalRowsData: SsrTotalRows = this.rptService.ssrTotalRowsData;

    const rowsPerPage: number = this.serverSideRendering.rowsPerPage;
    const totalRows = totalRowsData.filtered ? totalRowsData.filtered : totalRowsData.total;

    let totalPagesToFetch: number = totalRows / rowsPerPage;
    totalPagesToFetch = Math.ceil(totalPagesToFetch);

    this.cService.promptConfig = {
      title: this.cService.readyTranslations.messagesAndWarnings.exportingData,
      body: `
      <p>${this.cService.readyTranslations.messagesAndWarnings.downloadingAndProcessingExport}</p>
      <p>${this.cService.readyTranslations.messagesAndWarnings.pleaseWait}...</p>
      `,

      cancelButtonText: this.cService.readyTranslations.generic.cancel,
      hideHeaderCloseButton: true,

      showQueryProgress: true,
    };

    const promptDialog = this.dialog.open(PromptWithActionDialogComponent, { disableClose: true });

    promptDialog.afterOpened().subscribe({
      next: () => {
        this.processServerSideReportDownload(totalPagesToFetch, rowsPerPage, totalRows, () => {
          if (this.serverDataDownloadComplete) {
            this.exportReportToRequestedExtension(extension);
          }

          if (this.serverDataDownloadFailed) {
            this.cService.showToastWithoutDuration(
              this.cService.readyTranslations.messagesAndWarnings.failedToFetchData,
              'close'
            );
          }

          this.dialog.closeAll();
        });
      },
    });

    promptDialog.afterClosed().subscribe({
      next: () => {
        const reload: boolean = totalPagesToFetch > 1;
        this.unsubscribeServerDataDownload(this.serverDataDownloadFailed, reload);
      },
    });
  }

  private processServerSideReportDownload(
    totalPagesToFetch: number,
    rowsPerPage: number,
    totalRows: number,
    doneOrFailedCallback?: () => void
  ) {
    let totalPagesFetched = 0;
    let compiledData = [];
    const intervalMs = 1000;
    const percentage: number = 100 / totalPagesToFetch;

    this.serverDataDownloadComplete = false;
    this.serverDataDownloadFailed = false;
    this.cService.promptConfig.determinateProgressPerc = 0;
    this.serverSideRendering.rowsPerPage = totalRows;

    const collatedRequests = this.reportService.commonReportBatch(
      this.reportOptions.reportEndpointName,
      totalPagesToFetch,
      rowsPerPage
    );

    timer(0, intervalMs)
      .pipe(take(totalPagesToFetch))
      .subscribe((index) =>
        collatedRequests[index]
          .pipe(
            take(1),
            tap((response) => {
              totalPagesFetched++;
              compiledData[index] = response.data;
              this.cService.promptConfig.determinateProgressPerc = totalPagesFetched * percentage;
            }),
            filter(() => totalPagesFetched === totalPagesToFetch),
            tap(() => {
              this.rptService.artificialSsrDataFeed = [].concat(...compiledData);
              this.activateServerSideRenderingOnDemand();
              this.agGrid.api.refreshServerSide();

              setTimeout(() => {
                this.serverDataDownloadComplete = true;

                if (doneOrFailedCallback) {
                  doneOrFailedCallback();
                }
              }, 4000);
            })
          )
          .subscribe({
            error: () => {
              this.cService.hideIndeterminateProgress = true;
              this.serverDataDownloadFailed = true;

              if (doneOrFailedCallback) {
                doneOrFailedCallback();
              }
            },
          })
      );
  }

  private unsubscribeServerDataDownload(failed?: boolean, forceReload: boolean = true) {
    const message = failed
      ? this.cService.readyTranslations.messagesAndWarnings.failedToFetchData
      : this.cService.readyTranslations.messagesAndWarnings.working + '...';

    this.cService.showToast(message);

    this.rptService.artificialSsrDataFeed = null;
    this.serverSideRendering.rowsPerPage = this.rptService.currentReportMeta.pagination.rowsPerPage;
    if (forceReload) {
      location.reload();
    }

    this.cService.hideIndeterminateProgress = true;
  }

  private activateServerSideRenderingOnDemand(setSsrOption: boolean = false, ssrStoreType = 'partial') {
    if (
      this.serverSideRendering &&
      this.serverSideRendering.useServerSideRendering &&
      this.serverSideRendering.dataSource
    ) {
      if (setSsrOption) {
        const serverSide = 'serverSide';

        if (this.gridOptions.rowModelType !== serverSide) {
          this.gridOptions.rowModelType = serverSide;
        }

        return;
      }

      if (this.agGrid && this.agGrid.api) {
        this.agGrid.api.setServerSideDatasource(this.serverSideRendering.dataSource);
      }
    }
  }

  private getQueryParamAndCollateFilters() {
    this.activeRoute.queryParams.subscribe({
      next: (queryParams: any) => {
        let cachedFilterModel = this.rptService.getCachedFilters();
        if (cachedFilterModel) {
          cachedFilterModel = this.gridStateService.unapplyStaleQueryFilters(cachedFilterModel);
          this.rptService.gridState.filterModel = cachedFilterModel;
        }

        const queryParamKeys = Object.keys(queryParams);
        const metaStructureKeys = Object.keys(this.metadata.structure);

        this.queryParamFilters = [];
        queryParamKeys.forEach((qParamKey: string) => {
          const filterAvailableOnReport = this.queriedFilterAvailableOnReport(qParamKey, metaStructureKeys);
          if (filterAvailableOnReport) {
            const filterValues = queryParams[qParamKey].includes(',')
              ? queryParams[qParamKey].split(',')
              : [queryParams[qParamKey]];
            const filterType = this.getFilterTypeViaColumnType(qParamKey);

            this.queryParamFilters[qParamKey] = this.buildQueryFilterObject(qParamKey, filterType, filterValues);
          }
        });

        this.rptService.gridState.filterModel = {
          ...this.rptService.gridState.filterModel,
          ...this.queryParamFilters,
        };

        this.gridStateService.setCurrentActiveQueryFilters(queryParamKeys);

        this.rptService.cacheFiltersAcrossClientInstances();

        console.log('%c Current Grid Filters', 'color: lime', this.rptService.gridState.filterModel);
      },
    });
  }

  private setDefaultFilters() {
    for (const [key, column] of Object.entries(this.metadata.structure)) {
      const defaultValue = (column as any).default;
      if (defaultValue) {
        this.rptService.gridState.filterModel[key] = this.buildQueryFilterObject(
          key,
          this.getFilterTypeViaColumnType(key),
          Array.isArray(defaultValue) ? defaultValue : [defaultValue]
        );
      }
    }

    this.rptService.cacheFiltersAcrossClientInstances();
  }

  /* EVENTS
  ----------- */
  toggleGridToolPane() {
    setTimeout(() => {
      if (this.agGrid.api.isSideBarVisible()) {
        this.agGrid.api.setSideBarVisible(false);
        this.gridToolPaneShowing = false;
      } else {
        this.agGrid.api.setSideBarVisible(true);
        this.agGrid.api.openToolPanel('customDetails');
        this.gridToolPaneShowing = true;
      }
    });
  }

  private queriedFilterAvailableOnReport(queryKey: string, metaStructKeys: Array<string>): boolean {
    return metaStructKeys.includes(queryKey);
  }

  private getFilterTypeViaColumnType(columnKey: string): string {
    const columnType = this.metadata.structure[columnKey].type;
    let agFilterType: string = '';

    if (columnType === 'text' || columnType === 'image') {
      agFilterType = 'set';
    }

    if (columnType === 'date') {
      agFilterType = 'date';
    }

    if (columnType === 'number') {
      agFilterType = 'number';
    }

    return agFilterType;
  }

  // Restore functionality once done with updates
  // TODO: Review and test this restoration with Keiron
  private applyNotEqualFilter(columnID: string, values: string[]): void {
    if (this.agGrid.api) {
      const filterInstance = this.agGrid.api.getFilterInstance(columnID);

      filterInstance.setModel(null); //.selectEverything();
      values[0] = values[0].split('not:').pop();
      // values.forEach((value) => { filterInstance.unselectValue(value); } );
      filterInstance.setModel({ values: [] });

      // filterInstance.applyModel();
      this.agGrid.api.onFilterChanged();
    }
  }

  private buildQueryFilterObject(columnID: string, type: string, values: string[]): any {
    let filterOperator, filterValue;

    if (type === 'set') {
      [filterOperator, filterValue] = this.getFilterOperatorAndValue(values[0]);

      if (filterOperator === 'notEqual') {
        this.applyNotEqualFilter(columnID, values);
        return;
      }

      const lowercaseValues = values.map((value) => value.toLowerCase());
      if (lowercaseValues.includes('blanks')) {
        values.splice(lowercaseValues.indexOf('blanks'), 1, null);
      }

      return {
        filterType: type,
        values: values,
      };
    }

    const hasAndOrOperator = ['AND', 'OR'].some((operator) => values[0].includes(operator));
    if (values.length > 1 || hasAndOrOperator) {
      const filterObject = {
        filterType: type,
        operator: this.getMultipleFilterConditionsOperator(values[0]),
      };

      if (hasAndOrOperator) {
        values = values[0].split(/AND|OR/);
      }

      values.forEach((value, index) => {
        [filterOperator, filterValue] = this.getFilterOperatorAndValue(value);

        if (type === 'date') {
          filterObject[`condition${index + 1}`] = {
            filterType: type,
            type: filterOperator,
            dateFrom: filterValue,
            dateTo: null,
          };
        } else {
          filterObject[`condition${index + 1}`] = {
            filterType: type,
            type: filterOperator,
            filter: filterValue,
          };
        }
      });

      return filterObject;
    }

    [filterOperator, filterValue] = this.getFilterOperatorAndValue(values[0]);

    if (type === 'date') {
      return {
        filterType: type,
        type: filterOperator,
        dateFrom: filterValue,
        dateTo: null,
      };
    }

    return {
      filterType: type,
      type: filterOperator,
      filter: filterValue,
    };
  }

  private getFilterOperatorAndValue(value: string): string[] {
    let filterValue = '';
    let filterOperator = 'equals';

    const filterParts = value.split(':');

    if (filterParts.length === 1) {
      filterValue = filterParts[0];
    } else {
      filterOperator = this.mapFilterOperator(filterParts[0]);
      filterValue = filterParts[1];
    }

    return [filterOperator, filterValue];
  }

  private mapFilterOperator(operator: string): string {
    const operatorMap = {
      not: 'notEqual',
      gt: 'greaterThan',
      gte: 'greaterThanOrEqual',
      lt: 'lessThan',
      lte: 'lessThanOrEqual',
    };
    return operatorMap[operator];
  }

  private getMultipleFilterConditionsOperator(value: string): string {
    if (value.includes('OR')) {
      return 'OR';
    }
    return 'AND';
  }

  clearRowSelections() {
    this.agGrid.api.deselectAll();
    this.rptService.selectedRows = [];
  }

  getRowSelection(event: SelectionChangedEvent) {
    const currentSelection = event.api.getSelectedRows();
    if (!currentSelection || currentSelection.length == 0) {
      this.rptService.selectedRows = [];
    }
    if (currentSelection && currentSelection.length > 0) {
      // NOTE: getSelectedRow returns no row when search is active.
      // As a result, it overrides any selectedRow during double-click.
      // For this reason, it is only allowed when it returns a row at minimum
      this.rptService.selectedRows = event.api.getSelectedRows();
    }

    // NOTE: This prevents the user from mistakenly unselecting
    // all existing multiple selections due to an accidental single click.
    if (this.rptService.selectedRows.length > 1) {
      this.gridOptions.suppressRowClickSelection = true;
    } else {
      this.gridOptions.suppressRowClickSelection = false;
    }
    if (!this.disableStationAssignment) {
      setTimeout(() => {
        if (!this.doubleClicked && this.rptService.selectedRows[0]) {
          this.rowSelected.emit(this.rptService.selectedRows[0].id);
          this.doubleClicked = false;
        }
      }, 180);
    }
  }

  cellDoubleClicked(event: { data: any }) {
    if (event.data.idProvisioning) {
      // only when it is used for self provisioning.
      this.userSelected.emit(event);
      this.doubleClicked = true;
      setTimeout(() => {
        this.doubleClicked = false;
      }, 1000);
      return;
    }

    if (this.canDrilldownToDriverPerformanceRecord) {
      if (event.data && event.data.pin) {
        // Allow current selection drilldown on double-click.
        // I.E. Don't clear existing selection - Ecolab request!
        // this.clearRowSelections();

        if (!this.cService.userProfile.manages || this.cService.userProfile.manages.length === 0) {
          console.log('%cCell double-click event', 'color: limegreen', event.data);

          const driverAlreadyAdded = this.rptService.selectedRows.find((row) => row.pin === event.data.pin);

          if (!driverAlreadyAdded) {
            this.rptService.selectedRows.push(event.data);

            this.rptService.selectedRows.forEach((driverData: any) => {
              const selectedRowIndex: number = this.rawData.findIndex((data) => data.pin == driverData.pin);
              if (this.agGrid.api) {
                this.agGrid.api.getDisplayedRowAtIndex(selectedRowIndex).setSelected(true);
              }
            });
          }
        } else {
          this.rptService.autoPopulateManagedDriversForManagement(this.rawData, this.gridOptions);
        }

        this.makeSelectedRowEditableViaDialog(event.data);
      }
    }
  }

  searchTextChanged() {
    setTimeout(() => {
      this.agGrid.api.setQuickFilter(this.rptService.gridSearch.text);
    });
  }

  private searchOrClearServerSideSearch() {
    if (this.serverSideRendering) {
      if (this.rptService.gridSearch.text !== this.rptService.cachedSsrSearchText) {
        this.agGrid.api.refreshServerSide();
      }
    }
  }

  clearSearch() {
    this.rptService.gridSearch.text = '';
    this.rptService.gridSearch.activated = false;

    if (this.agGrid.api.isQuickFilterPresent()) {
      this.agGrid.api.setQuickFilter('');
    }

    this.searchOrClearServerSideSearch();
  }

  private updateGridStateSortModel(): void {
    this.rptService.gridState.sortModel = Object.assign(
      this.rptService.gridState.sortModel,
      this.agGrid.columnApi.getColumnState()
    );

    this.rptService.gridState.sortModel.map((model: ColumnSortModel) => {
      model.colId = model.colId.replace(this.cService.regEx.underscoreAndCharsAfter, '');
    });
  }

  gridSortChanged() {
    if (!this.initialLoad) {
      this.updateGridStateSortModel();

      this.rptService.persistedStateChanged = true;
      console.log('%c Sort Model', 'color: orange', this.rptService.gridState.sortModel);

      // Show or hide back-end's default sort indicator
      setTimeout(() => {
        const dummySortIndicators = document.querySelectorAll('.dummy-sort-indicator');
        if (dummySortIndicators) {
          if (this.rptService.gridState.sortModel && this.rptService.gridState.sortModel.length > 0) {
            dummySortIndicators.forEach((dummySortIndicator: Element) => {
              dummySortIndicator.classList.add('ag-hidden');
            });
          } else {
            dummySortIndicators.forEach((dummySortIndicator: Element) => {
              dummySortIndicator.classList.remove('ag-hidden');
            });
          }
        }
      });
    }
  }

  private restoreDefaultFilters() {
    // TODO: Confirm that all default filters now come through the meta data
    // and then remove this method and rptService.defaultFilters
    const defaultFilterKeys = Object.keys(this.rptService.defaultFilters);

    defaultFilterKeys.forEach((defaultFilterKey: string) => {
      if (this.currentFilters[defaultFilterKey]) {
        this.rptService.gridState.filterModel[defaultFilterKey] = this.currentFilters[defaultFilterKey];
      } else {
        if (!this.rptService.gridState.filterModel[defaultFilterKey]) {
          // Back to default
          this.rptService.gridState.filterModel[defaultFilterKey] = this.rptService.defaultFilters[defaultFilterKey];
        }
      }
    });
  }

  private updateGridStateFilterModel(clearedOutFilters: any): void {
    clearedOutFilters.forEach((clearFilterKey) => {
      delete this.rptService.gridState.filterModel[clearFilterKey];
    });

    this.rptService.gridState.filterModel = Object.assign(this.rptService.gridState.filterModel, this.currentFilters);
  }

  gridColumnFilterModified() {
    const userModifiedFilter = !this.initialLoad;

    if (userModifiedFilter) {
      setTimeout(() => {
        console.log('%c Grid Filter Model Modified', 'color: lightblue', this.agGrid.api.getFilterModel());

        const filterModelBeforeModified = this.currentFilters;
        this.currentFilters = this.agGrid.api.getFilterModel();

        const filterModelBeforeModifiedKeys = Object.keys(filterModelBeforeModified);
        const currentFilterKeys = Object.keys(this.currentFilters);

        const clearedOutFilters = filterModelBeforeModifiedKeys.filter(
          (filterKey) => !currentFilterKeys.includes(filterKey)
        );

        this.updateGridStateFilterModel(clearedOutFilters);

        const lastCurrentFilterItem = currentFilterKeys.length - 1;
        this.originalKeyHasUnderscore =
          currentFilterKeys.length > 0 && currentFilterKeys[lastCurrentFilterItem].includes('_');
      }, 1000);
    }

    setTimeout(() => {
      this.restoreDefaultFilters();

      // Handle agGrid's underscore suffix to ensure persistence
      // on identical column names for newly loaded reports.
      const modelKeys = Object.keys(this.rptService.gridState.filterModel);

      if (this.originalKeyHasUnderscore) {
        // Create non-underscored properties
        modelKeys.forEach((originalKey: string) => {
          const key = originalKey.replace(this.cService.regEx.underscoreAndCharsAfter, '');
          this.rptService.gridState.filterModel[key] = this.rptService.gridState.filterModel[originalKey];
        });
      } else {
        // Create underscored properties
        modelKeys.forEach((originalKey: string) => {
          const _key = originalKey.replace(this.cService.regEx.underscoreAndCharsAfter, '') + '_1';
          this.rptService.gridState.filterModel[_key] = this.rptService.gridState.filterModel[originalKey];
        });
      }

      this.rptService.cacheFiltersAcrossClientInstances();
      this.rptService.persistedStateChanged = true;
      console.log('%c - :: - Filter Model Changed', 'color: limegreen', this.rptService.gridState.filterModel);
    }, 1500);
  }

  gridColumnFilterChanged() {
    if (this.metadata?.information?.pinnedBottomRow?.type === 'total' && !this.metadata.usesServerSideRendering) {
      this.updatePinnedTotalRowOnFilter();
    }
  }

  updatePinnedTotalRowOnFilter() {
    const currentTotalRow = this.pinnedBottomRowData.pop();

    if (currentTotalRow) {
      const reportNumericColumns = Object.keys(this.metadata.structure).filter((columnID) => {
        return this.metadata.structure[columnID].type === 'number';
      });

      reportNumericColumns.forEach((column) => {
        currentTotalRow[column] = 0;
      });

      this.agGrid.api.forEachNodeAfterFilter((rowNode, index) => {
        if (
          this.metadata?.information?.benchmarkRow &&
          rowNode.data[this.metadata.information.benchmarkRow.flaggedColumn]?.toLowerCase() === 'benchmark'
        ) {
          return;
        }

        reportNumericColumns.forEach((columnID) => {
          const referenceColumnID =
            this.metadata?.structure[columnID]?.columnDefinition?.filterValueGetter?.replace(':', '') ?? columnID;
          currentTotalRow[columnID] += rowNode.data[referenceColumnID];
        });
      });

      this.pinnedBottomRowData.push(currentTotalRow);

      this.agGrid.api.setPinnedBottomRowData(this.pinnedBottomRowData);
    }
  }

  gridVisibleColumnsToggled() {
    setTimeout(() => {
      const columnState = this.agGrid.columnApi.getColumnState();

      // Add / Remove columns from hidden-column list
      columnState.forEach((column: ColumnState) => {
        // NOTE: agGrid would suffix columnIds with _1 whenever new report is loaded and
        // similar column names to the previous report are found. We then remove the suffix
        // as we are able to process column persistence via the report service and before
        // agGrid gets to apply the suffix
        this.updateGridStateVisibleColumns(column);
      });

      this.rptService.persistedStateChanged = true;

      console.log('%c Hidden Columns', 'color: pink; font-size: 16px', this.rptService.gridState.hiddenColumns);
    });
  }

  private updateGridStateVisibleColumns(column: ColumnState): void {
    let columnId = '';

    if (column.colId.startsWith('course_')) {
      columnId = column.colId;
    } else {
      columnId = column.colId.replace(this.cService.regEx.underscoreAndCharsAfter, '');
    }

    const existsInOverrideVisibilityList = this.rptService.gridState.overrideAPIColumnsVisibility.find(
      (col) => col === columnId
    );
    const existsInHiddenList = this.rptService.gridState.hiddenColumns.find((col) => col === columnId);

    if (this.rptService.dataStructure[columnId] && this.rptService.dataStructure[columnId].hide) {
      if (!column.hide && !existsInOverrideVisibilityList) {
        this.rptService.gridState.overrideAPIColumnsVisibility.push(columnId);
      }

      if (column.hide && existsInOverrideVisibilityList) {
        this.rptService.gridState.overrideAPIColumnsVisibility =
          this.rptService.gridState.overrideAPIColumnsVisibility.filter((col) => col != columnId);
      }
    }

    if (column.hide && !existsInHiddenList) {
      this.rptService.gridState.hiddenColumns.push(columnId);
    }

    if (!column.hide && existsInHiddenList) {
      this.rptService.gridState.hiddenColumns = this.rptService.gridState.hiddenColumns.filter(
        (col) => col !== columnId
      );
    }
  }

  saveOrPersistCurrentStateAsDefault() {
    console.log('%c SAVE Report State', 'color: limegreen', this.rptService.gridState);

    this.rptService.saveReportState().subscribe({
      next: () => {
        this.rptService.persistedStateChanged = false;
        this.rptService.gridStateDifferentFromDefault = true;
      },
    });
  }

  resetReportState() {
    console.log('%c RESET Report State', 'color: limegreen', this.rptService.gridState);

    this.rptService.resetReportState();
  }

  gridColumnResized() {
    this.agGrid.api.setHeaderHeight(this.rptService.agGridConfig.common.headerHeight);
    const columns = this.agGrid.columnApi.getColumns();

    // Calculate need for column-heading text to span 3 rows
    for (let i = 0; i < columns.length; i++) {
      const column: Column = columns[i];
      const columnHeaderName = column.getColDef().headerName;

      if (columnHeaderName) {
        const columnHeaderWords = columnHeaderName.split(' ');
        const hasOverTwoWords: boolean = columnHeaderWords.length > 2;
        const columnActualWidth: number = column.getActualWidth();
        const pixelPerChar: number = 7.1;
        const langCode =
          this.cService.userProfile && this.cService.userProfile.languageCode
            ? this.cService.userProfile.languageCode.toLowerCase()
            : '';

        if (hasOverTwoWords) {
          const initialHeight: number = 50;
          let colHeaderHeight: number = initialHeight;

          // Check if first word pair fit column size
          const firstWordPair = columnHeaderWords[0] + ' ' + columnHeaderWords[1];
          let remainingWords = '';
          for (let x = 2; x < columnHeaderWords.length; x++) {
            remainingWords += ' ' + columnHeaderWords[x];
          }

          // Assume 1 char needs 7.1px of space
          const firstPairLength = firstWordPair.split('').length;
          const remainingWordsLength = remainingWords.split('').length;
          const maxCharPossibleInActualWidth = (columnActualWidth - 20) / pixelPerChar;

          if (remainingWordsLength > maxCharPossibleInActualWidth) {
            colHeaderHeight = 85;
            this.setColumnHeaderHeight(colHeaderHeight);
            break;
          } else if (firstPairLength > maxCharPossibleInActualWidth) {
            colHeaderHeight = 65;
          } else if (firstPairLength >= maxCharPossibleInActualWidth - 2.5) {
            colHeaderHeight = 60;
          }

          if (colHeaderHeight > initialHeight) {
            this.setColumnHeaderHeight(colHeaderHeight);
          }
        } else {
          if (langCode === 'jp' || langCode === 'kr' || langCode === 'ko') {
            this.setColumnHeaderHeight(60);
          }
        }
      }
    }
  }

  setColumnHeaderHeight(colHeaderHeight: number) {
    setTimeout(() => {
      this.agGrid.api.setHeaderHeight(colHeaderHeight);
    });
  }

  // Event emitter
  /**
   * Emit upload elements to the parent component
   */
  onUploadElements() {
    if (this.canUploadElements) {
      this.upload.emit({
        maxHeight: this.cService.isMobileViewport ? '100%' : '100%',
        maxWidth: this.cService.isMobileViewport ? '100%' : '100%',
        height: this.cService.isMobileViewport ? '100%' : '',
        width: this.cService.isMobileViewport ? '100%' : '95%',
        disableClose: true,
      });
    } else {
      this.cService.showToast(this.cService.readyTranslations.messagesAndWarnings.functionalityNotAvailable);
    }
  }

  /**
   *  Emit add element event to the parent component
   */
  onAddElement() {
    if (this.canAddElement) {
      this.addNewElement.emit({
        maxHeight: this.cService.isMobileViewport ? '100%' : '',
        maxWidth: this.cService.isMobileViewport ? '100%' : '',
        height: this.cService.isMobileViewport ? '100%' : '600px',
        width: this.cService.isMobileViewport ? '100%' : '95%',

        data: {
          selectedRow: {},
          reportEndpointName: this.reportOptions.reportEndpointName,
        },

        disableClose: true,
      });
    } else {
      this.cService.showToast(this.cService.readyTranslations.messagesAndWarnings.functionalityNotAvailable);
    }
  }

  onAddEntity() {
    this.administerMisContent.emit(this.cContentService.contentAdminDialogSettings);
  }

  /**
   * Emit delete element event to the parent component
   */
  onDeleteElement() {
    const defaultSelection = this.rptService.selectedRows[0];
    if (this.canDelete && defaultSelection) {
      this.deleteElement.emit(defaultSelection);
    } else {
      this.cService.showToast(this.cService.readyTranslations.messagesAndWarnings.functionalityNotAvailable);
    }
  }

  structure: {
    selected: {
      name: '';
      cellRenderer: 'selectUser';
      width: '40';
    };
    name: {
      name: 'Name';
      type: 'text';
      width: '200';
    };
    email: {
      name: 'Email';
      type: 'email';
      width: '200';
    };
    station: {
      name: 'Station';
      type: 'number';
      width: '200';
    };
    manages: {
      name: 'Manages';
      cellRenderer: 'managerGroups';
      wrapText: true;
    };
    firstLogin: {
      name: 'APP First Login';
      cellRenderer: 'sendActivationMail';
    };
    lastLogin: {
      name: 'APP Last Login';
      type: 'text';
    };
  };

  private exportReportAsExcel() {
    const customizedData = this.rptService.getExportDataCustomization(this.metadata.structure);
    this.removeNonExportableColumns(customizedData);
    if (typeof Worker !== 'undefined') {
      const worker = new Worker(new URL('../../app.worker', import.meta.url), { type: 'module' });
      worker.postMessage({ process: this.agGrid.api.exportDataAsExcel(customizedData) });
    } else {
      // Web Worker not supported in this environment.
      this.agGrid.api.exportDataAsExcel(customizedData);
    }
  }

  private exportReportAsCsv() {
    const customizedData = this.rptService.getExportDataCustomization(this.metadata.structure, true);
    this.removeNonExportableColumns(customizedData);
    if (typeof Worker !== 'undefined') {
      const worker = new Worker(new URL('../../app.worker', import.meta.url), { type: 'module' });
      worker.postMessage({ process: this.agGrid.api.exportDataAsCsv(customizedData) });
    } else {
      // Web Workers are not supported in this environment.
      this.agGrid.api.exportDataAsExcel(customizedData);
    }
  }

  //https://vrm-mentor.atlassian.net/browse/V3DDP-514
  private removeNonExportableColumns(customizedData: any) {
    if (this.metadata.name === 'Provisioning') {
      const columnsKeys = ['name', 'email', 'station', 'manages', 'firstLogin', 'lastLogin'];
      const columnsKeysWithRealStaff = [
        'name',
        'email',
        'realStaffNumber',
        'station',
        'manages',
        'firstLogin',
        'lastLogin',
      ];

      customizedData.columnKeys =
        this.cService.userProfile.companyOptionalSettings.displayRealStaffNumber === 'TRUE'
          ? columnsKeysWithRealStaff
          : columnsKeys;
      customizedData.allColumns = false;
    }
  }

  private exportReportToRequestedExtension(extension: DataExportExt) {
    if (extension.toLowerCase() === FileExtensionEnum.excel) {
      this.exportReportAsExcel();
    } else {
      this.exportReportAsCsv();
    }
  }

  makeSelectedRowEditableViaDialog(suggestedDefaultSelection?: any) {
    const defaultSelection = suggestedDefaultSelection ? suggestedDefaultSelection : this.rptService.selectedRows[0];
    this.rptService.currentReportData = this.rawData;
    if (defaultSelection) {
      this.makeRowEditable.emit({
        maxHeight: this.cService.isMobileViewport ? '100%' : '',
        maxWidth: this.cService.isMobileViewport ? '100%' : '',
        height: '100%',
        width: this.cService.isMobileViewport ? '100%' : '95%',
        data: {
          selectedRow: defaultSelection,
          reportEndpointName: this.reportOptions.reportEndpointName,
        },

        disableClose: true,
      });
    } else {
      console.error('VRM ERROR: Cannot drilldown without valid data selection');
    }
  }

  /* METHODS
  ----------- */

  private displayColumnsByViewportSize() {
    if (this.cService.isMobileViewport) {
      this.agGrid.api.closeToolPanel();
    }

    if (this.agGrid.columnApi.isPivotMode) {
      this.agGrid.columnApi.setPivotMode(false);
    }

    this.rptService.currentReportOptions = this.reportOptions;
    this.rptService.shouldDisplayMassCoachingActions = this.metadata.structure.hasOwnProperty('coachingActions');

    this.rptService.dataStructure = this.metadata.structure;
    this.columnsToDisplay = this.rptService.convertToAgColumnDefinition(this.metadata.structure, this.metadata.name);

    this.translate.get(['REPORTINFORMATION', 'GENERIC']).subscribe({
      next: (translation) => {
        this.reportHasInformation = false;
        this.reportInformation = this.setReportInformation(translation);
      },
    });
    if (!this.rawData) {
      this.rawData = [];
    } //workaround for V3DDP-428. It should be fixed in the API - Jay
    if (this.rawData.length === 0) {
      this.pinnedTopRowData = [];
      this.pinnedBottomRowData = [];
    }

    setTimeout(() => {
      this.rptService.currentReportData = this.rawData;

      if (!this.initialLoad) {
        this.agGrid.columnDefs = this.columnsToDisplay;
      }

      this.restoreStateAcrossGrids();
      this.gridToolPaneShowing = this.agGrid.api.isSideBarVisible();
    });

    this.makeGridVisibleOnceClientFilterDone();
  }

  setReportInformation(translation: any): ReportInformation | null {
    const information: ReportInformation = {};
    const metaData = this.metadata.information as ReportInformation;

    if (metaData) {
      // Notification
      if (metaData.notification) {
        information.notification = translation.REPORTINFORMATION[metaData.notification];
        this.reportHasInformation = true;
      }

      // Intervention Reports
      if (metaData.recommendationsReport) {
        information.recommendationsReport = {
          intervention: metaData.recommendationsReport.intervention,
          dates: {
            timezone: metaData.recommendationsReport.dates.timezone,
            generated: metaData.recommendationsReport.dates.generated,
            refreshed: metaData.recommendationsReport.dates.refreshed,
          },
        };
        this.reportHasInformation = true;
      }
    }

    return information;
  }

  private attemptToFitGridColumns() {
    const visibleColumns = this.columnsToDisplay.filter((col) => !col.hide);

    // Run Async - Ensure grid is attached to the DOM
    setTimeout(() => {
      visibleColumns.forEach((columnDef: ColDef) => {
        if (!columnDef.width) {
          this.agGrid.columnApi.autoSizeColumn(columnDef.colId);
        }
      });

      // NOTE: Called manually in case autoSizeColumn() wasn't triggered
      this.gridColumnResized();
    });

    // NOTE: Sizing columns to fit creates inconsistent sizes when
    // moving from report to report. Allocated sizes are even ignored.

    // setTimeout(() => {
    //   if (this.cService.isPcViewport && visibleColumns.length < 18) {
    //     this.agGrid.api.sizeColumnsToFit();
    //   }
    // }, 250);
  }

  private restoreStateAcrossGrids() {
    if (!this.initialLoad) {
      // NOTE: When a new report is loaded, columns missing in the previous
      // report in view are assumed to come at later index in the column ordering.
      // This solution forces agGrid to use the order returned
      // from the API after report switch.
      setTimeout(() => {
        if (this.metadata && this.metadata.structure) {
          const columnKeys = Object.keys(this.metadata.structure);
          this.agGrid.columnApi.moveColumns(columnKeys, 0);
        }
      });
    }

    // Search / QuickFilter
    if (this.rptService.gridSearch.text) {
      this.searchTextChanged();
    }

    // Filter / Column Filter
    if (this.rptService.gridState.filterModel) {
      const filterModelForCurrentReport = this.rptService.getGridStateFilterModelForCurrentReport();

      setTimeout(() => {
        // If it's not possible to set this from the reports-service, then agGrid's _1 suffix
        // would need to be taken into account for all filterModel column names.
        this.agGrid.api.setFilterModel(filterModelForCurrentReport);
      });

      // Try again if SSR in case of Async Filter data which would have been late to arrive
      // after the initial attempt above to set the filter.
      if (this.serverSideRendering) {
        setTimeout(() => {
          this.agGrid.api.setFilterModel(filterModelForCurrentReport);
        }, 1500);
      }
    }

    this.attemptToFitGridColumns();
  }

  private makeGridVisibleOnceClientFilterDone() {
    setTimeout(() => {
      this.showRenderedGrid = true;
    }, 650);
  }

  private hideGridInitially() {
    const exemptedReports: Array<string> = ['RiskCoach'];

    const currentReport: string = this.rptService.currentReportMeta ? this.rptService.currentReportMeta.name : '';

    if (!exemptedReports.includes(currentReport)) {
      this.showRenderedGrid = false;
    }
  }

  closeResolveAlertSideNav(): void {
    this.rptService.showReportSideNav = false;
    this.rptService.resolveAlertButtonText = this.cService.readyTranslations.orderMvr.orderMvrNow;
    this.rptService.resolveAlertHasError = false;
    this.sidenav.close();
  }

  /**
   * Deletes rows from the grid with the provided indexes.
   */
  deleteRows(rowData: any) {
    if (this.agGrid.api && rowData) {
      this.agGrid.api.applyTransaction({
        remove: Array.isArray(rowData) ? rowData : [rowData],
      });
    }
  }

  /**
   * Adds rows in the grid.
   */
  addRow(rowData: any) {
    if (rowData) {
      this.agGrid.api.applyTransaction({
        add: Array.isArray(rowData) ? rowData : [rowData],
      });
    }
  }

  /**
   * Updates rows in the grid.
   */
  updateRow(rowData: any) {
    if (rowData) {
      this.agGrid.api.applyTransaction({
        update: Array.isArray(rowData) ? rowData : [rowData],
      });
    }
  }

  ngOnDestroy(): void {
    this.resetAddNewlySelectedRowBehaviourSubject();
    this.cService.releaseMultipleSubscriptions(this.subscriptions);
  }

  private resetAddNewlySelectedRowBehaviourSubject(): void {
    this.rptService.addNewlySelectedRowAndTriggerObservable(null);
  }
}
